import React, { useState, useReducer, useEffect, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Table,
  message,
  Row,
  Col,
  Select,
} from "antd";

import {
  FETCH_ALL_ARTICLE_API,
  FETCH_ALL_COLOR_API,
  FETCH_ALL_COUNTER_API,
  FETCH_ALL_SHIPPING_API,
  FETCH_ALL_STOCK_API,
} from "../../../Constants/Apis";
import { useFetch } from "../../../Utils/useFetch";
import { reducer } from "../../../Utils/CommonUtils";
import { updateStock } from "../../../Services/StockService";
import {
  updateShipping,
  updateShippingSJ,
} from "../../../Services/ShippingService";

import StockValidationForm from "../../elements/Forms/StockValidationForm/StockValidationForm";

const StockValidationPage = () => {
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [items, setItems] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);

  const initialState = {
    searchKeyword: "",
    filteredInfo: {},
    sorter: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  // Default fetchUrl before a "Check" is triggered.
  const defaultFetchUrl = `${FETCH_ALL_STOCK_API}/sj/`;
  const [fetchUrl, setFetchUrl] = useState(defaultFetchUrl);

  // (Optional) Fetch counter data if needed.
  const { fetchedData: counterFetchedData = {} } = useFetch(
    FETCH_ALL_COUNTER_API,
    state
  );
  const { fetchedData: SJFetchedData = {} } = useFetch(
    FETCH_ALL_SHIPPING_API + "/current",
    state
  );

  const counterData = counterFetchedData?.data || [];
  const SJData = SJFetchedData?.data || [];

  const [barcodeValue, setBarcodeValue] = useState(""); // Store barcode value
  const [scannerActive, setScannerActive] = useState(false); // Track if the scanner is active

  const handleScanBarcode = (err, result) => {
    if (result) {
      setBarcodeValue(result.text); // Set barcode field value when scanned
      setScannerActive(false); // Stop the scanner after scan
      modalForm.setFieldsValue({ barcode: result.text }); // Set the barcode in the modal form field
    } else {
      setBarcodeValue("Not Found");
    }
  };

  // Use our custom hook to fetch stock data.
  const {
    fetchedData: { data: StockData = [] },
    callReFetch,
    loading,
  } = useFetch(fetchUrl, state);

  console.log("State being sent to useFetch:", state); // Add this line

  // This ref is used to ensure we only warn once per fetchUrl.
  const warnedFetchUrlRef = useRef(null);

  // When fetchUrl changes, trigger a re-fetch.
  useEffect(() => {
    if (fetchUrl) {
      callReFetch();
    }
  }, [fetchUrl]);

  // useEffect(() => {

  //   if (fetchUrl !== defaultFetchUrl && !loading) {
  //     if (StockData && StockData.length === 0) {
  //       // Warn only once per lookup.
  //       if (warnedFetchUrlRef.current !== fetchUrl) {
  //         if (StockData.length === 0) {
  //           message.warning("No stock data found for this Surat Jalan!");
  //         }
  //         warnedFetchUrlRef.current = fetchUrl;
  //       }
  //       setItems([]);
  //     } else if (StockData && StockData.length > 0) {

  //       setItems(StockData);
  //       // Reset the warning tracker if data is found.
  //       warnedFetchUrlRef.current = null;
  //     }
  //   }
  // }, [StockData, fetchUrl]);

  useEffect(() => {
    if (StockData !== undefined) {
      if (fetchUrl !== defaultFetchUrl && !loading) {
        if (StockData.length === 0) {
          // Warn only once per lookup.
          if (warnedFetchUrlRef.current !== fetchUrl) {
            message.warning("No stock data found for this Surat Jalan!");
            warnedFetchUrlRef.current = fetchUrl;
          }
          setItems([]);
        } else if (StockData.length > 0) {
          setItems(StockData);
          // Reset the warning tracker if data is found.
          warnedFetchUrlRef.current = null;
        }
      }
    }
  }, [StockData, fetchUrl, defaultFetchUrl, loading]);

  // Calculate and set the total quantity whenever items change.
  useEffect(() => {
    const totalQuantity = items.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0
    );
    form.setFieldsValue({ totalQuantity });
  }, [items, form]);

  // When the "Check" button is clicked, update the fetchUrl.
  const fetchSJData = async () => {
    try {
      const values = await form.validateFields(["noSJ"]);
      const newFetchUrl = `${FETCH_ALL_STOCK_API}/sj/${values.noSJ}`;
      setFetchUrl(newFetchUrl);
    } catch (error) {
      message.error("Error fetching data");
    }
  };

  // Handle add or edit item in the modal.
  const handleAddOrEditItem = async () => {
    try {
      const values = await modalForm.validateFields();
      setItems((prevStockData) =>
        prevStockData.map((item) =>
          item.barcode === modalForm.getFieldValue("barcode")
            ? {
                ...item,
                currentQuantity: values.currentQuantity,
                keterangan: values.keterangan,
              }
            : item
        )
      );

      modalForm.resetFields();
      setIsModalVisible(false);
      setEditingItemIndex(null);
    } catch (error) {
      console.error("Validation Failed:", error);
    }
  };

  // Open the modal to edit a specific item.
  const handleEditItem = (index) => {
    setEditingItemIndex(index);
    modalForm.setFieldsValue(items[index]);
    setIsModalVisible(true);
  };

  // Delete an item.
  const handleDeleteItem = (index) => {
    setItems((prevItems) => prevItems.filter((_, i) => i !== index));
    message.success("Item deleted successfully");
  };

  // Handle final form submission.
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (items.length === 0) {
        message.error("You must add at least one item!");
        return;
      }

      await Promise.all(
        items.map((item) =>
          updateStock(item._id, {
            noSJ: values.noSJ,
            barcode: item.barcode || "",
            currentQuantity: Number(item.currentQuantity) || 0,
            status: "in stock",
            keterangan: item.keterangan || "",
          })
        )
      );

      updateShippingSJ(values.noSJ, {
        status: "in stock",
        dateReceived: new Date(),
        keterangan: values.keterangan,
      });

      message.success("Stock details submitted successfully!");
      form.resetFields();
      setItems([]);
    } catch (error) {
      console.error("Error submitting stock details:", error);
      message.error("Failed to submit stock details. Please try again.");
    }
  };

  const columns = [
    { title: "Barcode", dataIndex: "barcode", key: "barcode" },
    { title: "Article", dataIndex: "article", key: "article" },
    { title: "Warna", dataIndex: "color", key: "color" },
    { title: "Size", dataIndex: "size", key: "size" },
    { title: "Quantity", dataIndex: "quantity", key: "quantity" },
    {
      title: "currentQuantity",
      dataIndex: "currentQuantity",
      key: "currentQuantity",
    },
    {
      title: "Keterangan",
      dataIndex: "keterangan",
      key: "keterangan",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, __, index) => (
        <div>
          <Button
            onClick={() => handleEditItem(index)}
            style={{ marginRight: 10 }}
          >
            Edit
          </Button>
          <Button onClick={() => handleDeleteItem(index)} danger>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <h2>Create Stock</h2>
      <div style={{ width: "80vw" }}>
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="No Surat Jalan" name="noSJ" required>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select a No Surat Jalan"
                  onChange={fetchSJData}
                  // value={noSJ}
                >
                  {SJData.map((SJ) => (
                    <Select.Option key={SJ.noSJ} value={SJ.noSJ}>
                      {SJ.noSJ}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Keterangan" name="keteranganValidasi">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Total Pasang" name="totalQuantity">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>
          <Table
            columns={columns}
            dataSource={items}
            rowKey={(record, index) => index}
            pagination={false}
          />
          <Button type="primary" onClick={() => setIsModalVisible(true)}>
            Validate Item
          </Button>
          <Button type="primary" htmlType="submit" style={{ marginLeft: 10 }}>
            Submit
          </Button>
        </Form>
      </div>
      <Modal
        title={editingItemIndex === null ? "Add Item" : "Edit Item"}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleAddOrEditItem}>
            {editingItemIndex === null ? "Add" : "Save"}
          </Button>,
        ]}
      >
        <StockValidationForm
          modalForm={modalForm}
          handleManualInputChange={(e) => {
            const { name, value } = e.target;
            modalForm.setFieldsValue({ [name]: value }); // ✅ Dynamically update fields
          }}
          barcodeValue={modalForm.getFieldValue("barcode")}
          setScannerActive={setScannerActive}
          scannerActive={scannerActive}
          StockData={StockData}
          handleScanBarcode={(err, result) => {
            if (result) {
              modalForm.setFieldsValue({ barcode: result.text });
              setScannerActive(false);
            }
          }}
        />
      </Modal>
      ;
    </div>
  );
};

export default StockValidationPage;
